import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { SearchRegular, ArrowSyncRegular, DocumentTextRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { TasksApi, type TaskGridCategory, type TaskListKind } from "@/services/api/tasks-api";
import { useAuth } from "@/services/auth/use-auth";
import type { TriyoTask } from "@/types/models";
import { formatNumericCalendarDate } from "@/utils/calendar-date";
import { resolveLaunchUrl } from "@/utils/launch-url";
import { TabSectionHeader } from "@/components/Workflow/TabSectionHeader";
import { TASK_CATEGORIES, TASK_CATEGORY_LABELS, taskStatusKey, type TaskCategory } from "./task-categories";
import "../shared.scss";
import "./Tasks.scss";

const STATUS_BADGES: Record<string, { bg: string; fg: string; label: string }> = {
  in_progress: { bg: "#F7EBFF", fg: "#7405C0", label: "In Progress" },
  not_started: { bg: "#F0F0F0", fg: "#555555", label: "Not Started" },
  overdue: { bg: "#FCE9E2", fg: "#C03E06", label: "Overdue" },
  completed: { bg: "#EBF5ED", fg: "#045912", label: "Completed" },
  archived: { bg: "#F0F0F0", fg: "#555555", label: "Archived" },
};

const CATEGORY_REQUEST: Record<TaskCategory, TaskGridCategory> = {
  active: "Active",
  completed: "Completed",
  archived: "Archived",
};

function taskTitle(task: TriyoTask): string {
  return task.Name || task.Title || task.TaskName || task.Subject || "Untitled task";
}

function formatDueDate(task: TriyoTask): string {
  const raw = task.Due || task.DueDate || task.EndDate;
  if (!raw) return "—";
  return formatNumericCalendarDate(raw);
}

function StatusBadge({ statusKey }: { statusKey: string }): JSX.Element {
  const palette = STATUS_BADGES[statusKey] ?? STATUS_BADGES.not_started;
  return (
    <span className="task-status" style={{ background: palette.bg, color: palette.fg }}>
      {palette.label}
    </span>
  );
}

/**
 * Tasks tab. Loads the complete selected category from the same static or
 * configurable Telerik-grid endpoint selected by Triyo.Web for this company.
 */
export function Tasks(): JSX.Element {
  const { user } = useAuth();
  const [category, setCategory] = useState<TaskCategory>("active");
  const [tasks, setTasks] = useState<TriyoTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [spinning, setSpinning] = useState(false);
  const requestRef = useRef(0);
  const taskListKindRef = useRef<{ companyId?: number; promise: Promise<TaskListKind> } | null>(null);

  const load = useCallback(
    async (nextCategory: TaskCategory) => {
      const requestId = ++requestRef.current;
      setLoading(true);
      setError(null);
      try {
        const companyId = user?.CompanyId;
        if (!taskListKindRef.current || taskListKindRef.current.companyId !== companyId) {
          taskListKindRef.current = {
            companyId,
            promise: TasksApi.resolveTaskListKind(companyId),
          };
        }
        const kind = await taskListKindRef.current.promise;
        const rows = await TasksApi.getUserTasksForCategory(CATEGORY_REQUEST[nextCategory], kind);
        if (requestId !== requestRef.current) return;
        setTasks(rows);
      } catch (reason: unknown) {
        if (requestId !== requestRef.current) return;
        setTasks([]);
        setError(reason instanceof Error ? reason.message : "Tasks could not be loaded.");
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false);
        }
      }
    },
    [user?.CompanyId]
  );

  useEffect(() => {
    void load(category);
    return () => {
      requestRef.current += 1;
    };
  }, [category, load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((task) => {
      const title = taskTitle(task).toLowerCase();
      const project = (task.ProjectName || task.Project || "").toLowerCase();
      return title.includes(q) || project.includes(q);
    });
  }, [tasks, query]);

  const refresh = async () => {
    setSpinning(true);
    try {
      await load(category);
    } finally {
      setSpinning(false);
    }
  };

  const tasksUrl = resolveLaunchUrl("tasks");
  const label = TASK_CATEGORY_LABELS[category].toLowerCase();

  return (
    <div className="tab-panel">
      <TabSectionHeader
        title="Tasks"
        action={
          <button
            type="button"
            aria-label="Refresh"
            title="Refresh"
            className={`icon-button${spinning ? " icon-button--spin" : ""}`}
            disabled={spinning}
            aria-busy={spinning}
            onClick={() => void refresh()}
          >
            <ArrowSyncRegular />
          </button>
        }
      />

      <div className="list-tabs" role="tablist" aria-label="Task category">
        {TASK_CATEGORIES.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={category === id}
            className={`list-tab${category === id ? " list-tab--active" : ""}`}
            onClick={() => setCategory(id)}
          >
            {TASK_CATEGORY_LABELS[id]}
            {category === id && !loading ? <span className="list-tab__count">{tasks.length}</span> : null}
          </button>
        ))}
      </div>

      <div className="search-row">
        <div className="search-field">
          <SearchRegular />
          <input
            className="search-field__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for Tasks"
            aria-label="Search for Tasks"
          />
        </div>
      </div>

      <div className="list-scroll">
        {loading ? (
          <div className="empty-state" role="status" aria-live="polite">
            <FluentSpinner label="Loading tasks…" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="empty-state" role="alert">
            {error}
          </div>
        ) : null}

        {!loading && !error && visible.length === 0 ? (
          <div className="empty-state" role="status">
            No {label} Tasks{query ? " match your search" : ""}
          </div>
        ) : null}

        {!loading &&
          !error &&
          visible.map((task) => {
            const statusKey = taskStatusKey(task);
            return (
              <div key={task.Id ?? task.TaskId ?? taskTitle(task)} className="task-row">
                <div className="task-row__body">
                  <div className="task-row__left">
                    <span className="task-row__icon">
                      <DocumentTextRegular />
                    </span>
                    <span className="task-row__text">
                      <span className="task-row__title">{taskTitle(task)}</span>
                      <span className="task-row__due">Due Date: {formatDueDate(task)}</span>
                    </span>
                  </div>
                  <div className="task-row__right">
                    {statusKey === "overdue" ? (
                      <span className="task-row__action" title="Action required">
                        !
                      </span>
                    ) : null}
                    <StatusBadge statusKey={statusKey} />
                  </div>
                </div>
                <span className="list-row__divider" />
              </div>
            );
          })}
      </div>

      {tasksUrl ? (
        <div className="tasks-footer">
          <a className="tasks-footer__link" href={tasksUrl} target="_blank" rel="noreferrer">
            Switch to web application
          </a>
        </div>
      ) : null}
    </div>
  );
}
